import { useCallback, useContext, useEffect, useRef, useState } from "react";
import { onAuthStateChanged, signInWithCustomToken, signOut as firebaseSignOut } from "firebase/auth";
import { auth } from "../../services/firebase";
import { setSession, STORAGE_KEY, STORAGE_USER_KEY } from "./authUtils";
import { AuthContext } from "./authContext";
import { decrypt, encrypt } from "../../utils/customFunctions";

export function AuthProvider({children}){
    const [initialized, setInitialized] = useState(false);
    const [userAuthenticationVerified, setUserAuthenticationVerified] = useState(-1);
    const [currentUser, setCurrentUser] = useState(null);
    const [accessToken, setAccessToken] = useState(null);

    const initializedRef = useRef(false);
    const unsubscribeRef = useRef(null);

    const logOut = useCallback(async () => {
        try {
            await firebaseSignOut(auth);
        } catch (error) {
            console.error("Firebase signOut error:", error);
        }

        setCurrentUser(null);
        setAccessToken(null);
        setUserAuthenticationVerified(-1);
        setSession(null);
    }, []);

    const initialize = useCallback(async () => {
        try {
            const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
                if (firebaseUser) {
                    try {
                        const idToken = await firebaseUser.getIdToken(true);
                        setAccessToken(idToken);
                        setSession(idToken);

                        const userCipherText = localStorage.getItem(STORAGE_USER_KEY);
                        if (userCipherText) {
                            const userData = decrypt(userCipherText);
                            setCurrentUser(JSON.parse(userData));
                        } else {
                            setCurrentUser({ firebaseUid: firebaseUser.uid });
                        }
                    } catch (error) {
                        console.error("Failed to get Firebase ID token:", error);
                        setCurrentUser(null);
                        setAccessToken(null);
                        setSession(null);
                    }
                } else {
                    const storedToken = localStorage.getItem(STORAGE_KEY);
                    const userCipherText = localStorage.getItem(STORAGE_USER_KEY);

                    if (storedToken && userCipherText) {
                        setSession(storedToken);
                        try {
                            const userData = decrypt(userCipherText);
                            setCurrentUser(JSON.parse(userData));
                            setAccessToken("Bearer ".concat(storedToken));
                        } catch (error) {
                            console.error("Legacy decrypt failed:", error);
                            setCurrentUser(null);
                            setAccessToken(null);
                        }
                    } else {
                        setCurrentUser(null);
                        setAccessToken(null);
                    }
                }

                setUserAuthenticationVerified(1);
                if (!initializedRef.current) {
                    initializedRef.current = true;
                    setInitialized(true);
                }
            });

            unsubscribeRef.current = unsubscribe;
        } catch (error) {
            console.error("Auth init error:", error);
            logOut();
        }
    }, [logOut]);

    useEffect(() => {
        if (!initialized) {
            initialize();
        }

        return () => {
            if (unsubscribeRef.current) {
                unsubscribeRef.current();
                unsubscribeRef.current = null;
            }
            initializedRef.current = false;
        };
    }, [initialized, initialize]);

    const saveLoginResponse = useCallback(async (customResponse, mobileNumber) => {
        // Unwrap successResponse wrapper: { success, data: { ... } } → { ... }
        const responseData = customResponse.data || customResponse;
        const { token, staffId } = responseData;
        const customToken = token;
        console.log("Received custom token from login response:", customToken);
        if (customToken) {
            console.log("Attempting to sign in with custom token...");
            try {
                const userCredential = await signInWithCustomToken(auth, customToken);
      
                console.log("Successfully signed into Firebase via Phone!", userCredential);
                

                const user = {
                    "staffID": staffId,
                    "mobileNumber": mobileNumber
                };

                const currentFirebaseUser = auth.currentUser;
                console.log("Successfully signed into Firebase via Phone!", currentFirebaseUser);
                if (currentFirebaseUser) {
                    const idToken = await currentFirebaseUser.getIdToken();
                    setCurrentUser(user);
                    setAccessToken(idToken);
                    setSession(idToken);
                }

                const encryptedData = encrypt(JSON.stringify(user));
                localStorage.setItem(STORAGE_USER_KEY, encryptedData);

                setUserAuthenticationVerified(1);
            } catch (error) {
                console.error("Custom token sign-in failed:", error);
                handleLegacyLoginFallback(customResponse, mobileNumber);
            }
        } else {
            handleLegacyLoginFallback(customResponse, mobileNumber);
        }
    }, []);

    const handleLegacyLoginFallback = useCallback((customResponse, mobileNumber) => {
        const responseData = customResponse.data || customResponse;
        setSession(responseData.token);

        const user = {
            "staffID": responseData.staffId,
            "mobileNumber": mobileNumber
        };

        const encryptedData = encrypt(JSON.stringify(user));
        localStorage.setItem(STORAGE_USER_KEY, encryptedData);

        setCurrentUser(user);
        setAccessToken(responseData.accessToken);

        setUserAuthenticationVerified(1);
    }, []);

    const getUserClaims = useCallback(async () => {
        try {
            const user = auth.currentUser;
            if (user) {
                const idTokenResult = await user.getIdTokenResult();
                return idTokenResult.claims;
            }
            return null;
        } catch (error) {
            console.error("Failed to get user claims:", error);
            return null;
        }
    }, []);

    const value = {
        currentUser,
        accessToken,
        userAuthenticationVerified,
        logOut,
        saveLoginResponse,
        getUserClaims
    }

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
}

export const GetAuth = () => {
    return useContext(AuthContext);
};
